import * as React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';

// Helper function to format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Helper function to render status
const renderStatus = (status) => {
  const statusConfig = {
    'activo': { color: 'success', label: 'Activo' },
    'vencido': { color: 'error', label: 'Vencido' },
    'completado': { color: 'info', label: 'Completado' },
    'cancelado': { color: 'warning', label: 'Cancelado' },
    'pendiente': { color: 'default', label: 'Pendiente' },
    'pagado': { color: 'success', label: 'Pagado' }
  };

  const config = statusConfig[status] || { color: 'default', label: status };
  return <Chip label={config.label} color={config.color} size="small" />;
};

// Helper function to render payment status
const renderPaymentStatus = (status) => {
  const statusConfig = {
    'pendiente': { color: 'warning', label: 'Pendiente' },
    'pagado': { color: 'success', label: 'Pagado' },
    'vencido': { color: 'error', label: 'Vencido' }
  };

  const config = statusConfig[status] || { color: 'default', label: status };
  return <Chip label={config.label} color={config.color} size="small" />;
};

export default function PaymentsDataGrid({ todaysPayments, loading, error }) {
  
  // Define columns for the payments data grid
  const columns = [
    {
      field: 'numero_prestamo',
      headerName: 'No. Préstamo',
      flex: 0.8,
      minWidth: 100,
    },
    {
      field: 'cliente',
      headerName: 'Cliente',
      flex: 1.5,
      minWidth: 150,
    },
    {
      field: 'sucursal',
      headerName: 'Sucursal',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'producto',
      headerName: 'Producto',
      flex: 1.2,
      minWidth: 130,
    },
    {
      field: 'numero_cuota',
      headerName: 'Cuota',
      headerAlign: 'center',
      align: 'center',
      flex: 0.6,
      minWidth: 80,
    },
    {
      field: 'total_cuota',
      headerName: 'Monto Cuota',
      headerAlign: 'right',
      align: 'right',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: 'capital',
      headerName: 'Capital',
      headerAlign: 'right',
      align: 'right',
      flex: 1,
      minWidth: 100,
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: 'interes',
      headerName: 'Interés',
      headerAlign: 'right',
      align: 'right',
      flex: 1,
      minWidth: 100,
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: 'mora',
      headerName: 'Mora',
      headerAlign: 'right',
      align: 'right',
      flex: 1,
      minWidth: 100,
      renderCell: (params) => params.value > 0 ? formatCurrency(params.value) : '-',
    },
    {
      field: 'fecha_pago',
      headerName: 'Fecha Pago',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: 'estado_prestamo',
      headerName: 'Estado Préstamo',
      flex: 1,
      minWidth: 130,
      renderCell: (params) => renderStatus(params.value),
    },
    {
      field: 'estado_amortizacion',
      headerName: 'Estado Pago',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => renderPaymentStatus(params.value),
    },
  ];

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress size={60} />
        <Typography variant="h6">
          Cargando pagos del día...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          Error al cargar los pagos del día: {error}
        </Alert>
      </Box>
    );
  }

  const { data, total_count } = todaysPayments;

  if (!data || data.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        flexDirection: 'column',
        gap: 2
      }}>
        <Typography variant="h6" color="text.secondary">
          No hay pagos programados para hoy
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Total de pagos: {total_count || 0}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" component="h2">
          Pagos Programados para Hoy
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Total: {total_count} pagos
        </Typography>
      </Box>
      
      <DataGrid
        checkboxSelection
        rows={data}
        columns={columns}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        initialState={{
          pagination: { paginationModel: { pageSize: 20 } },
        }}
        pageSizeOptions={[10, 20, 50]}
        disableColumnResize
        density="compact"
        sx={{
          '& .even': {
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
          },
          '& .odd': {
            backgroundColor: 'transparent',
          },
        }}
        slotProps={{
          filterPanel: {
            filterFormProps: {
              logicOperatorInputProps: {
                variant: 'outlined',
                size: 'small',
              },
              columnInputProps: {
                variant: 'outlined',
                size: 'small',
                sx: { mt: 'auto' },
              },
              operatorInputProps: {
                variant: 'outlined',
                size: 'small',
                sx: { mt: 'auto' },
              },
              valueInputProps: {
                InputComponentProps: {
                  variant: 'outlined',
                  size: 'small',
                },
              },
            },
          },
        }}
      />
    </Box>
  );
}
